import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    viewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    AixSendMessageComponent,
    DocuSignForm,
    ErrorWrapper,
    eSignEnvelopeRecipient
} from '@trade-platform/ui-shared';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ProfileStoreFacade } from '@advisor-ui/app-services';
import { recipientType } from '@trade-platform/lib-enums';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixHeaderSectionComponent,
    AixInputComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { ENVIRONMENT, IEnvironment } from '@trade-platform/ui-utils';

import { AixSignerComponent } from '../signer/signer.component';
import { AixSignerFilterPipe } from '../pipes/signers.pipe';
import { isInProgress, RemoteData } from 'ngx-remotedata';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '@advisor-ui/orders';

@Component({
    selector: 'aix-docu-sign',
    templateUrl: './recipients.html',
    styleUrls: ['./recipients.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixHeaderSectionComponent,
        AixDataTestingDirective,
        AixButtonComponent,
        AixSignerComponent,
        FormsModule,
        ReactiveFormsModule,
        AixSignerFilterPipe,
        AixSendMessageComponent,
        AixInputComponent,
        AsyncPipe
    ]
})
export class AixOrderProcessDocuSignRecipientsComponent {
    private environment = inject<IEnvironment>(ENVIRONMENT);
    store = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    profileStore = inject(ProfileStoreFacade);

    sendMessageComponent = viewChild.required<AixSendMessageComponent>('sendMessageComponent');
    signers = input.required<eSignEnvelopeRecipient[]>();
    states = input.required<Observable<RemoteData<any, string | ErrorWrapper>>[]>();
    recipientError = input<string>('');
    recipients = computed(() => this.recipientError().split(','));

    // #5518: the shared send form previously gave no feedback while a send was in flight — the Send
    // button stayed enabled until the slow (N-envelope, for a bundle) `sendBundle`/`sendDocusign`
    // returned and the order reloaded. Derive an in-progress flag from the send RemoteData stream(s)
    // so the button disables and shows a "Sending…" state the instant the action dispatches (the store
    // flips the RemoteData to inProgress synchronously), which also guards against a double-send.
    isSending$: Observable<boolean> = toObservable(this.states).pipe(
        switchMap(states =>
            states.length
                ? combineLatest(states)
                : of([] as RemoteData<any, string | ErrorWrapper>[])
        ),
        map(states => states.some(isInProgress))
    );

    cancelESign = output();
    submitForm = output<DocuSignForm>();

    docusignEmail: string;

    recipientType = recipientType;

    subscriptions: Subscription[] = [];

    cancelEsignButtonType = BUTTON_TYPE.link;
    sendEsignButtonType = BUTTON_TYPE.primary;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        const environment = this.environment;

        this.docusignEmail = environment.contactDetails.docusignEmail;
    }

    editOrder() {
        this.store.actions.makeChanges.dispatch({ orderId: this.store.order.id });
    }

    onSubmit() {
        this.sendMessageComponent().sendForm.updateValueAndValidity();
        if (this.sendMessageComponent().sendForm.valid) {
            this.submitForm.emit(this.sendMessageComponent().sendForm.value);
        }
    }
}
